import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Alert, Button, LoadingSpinner, ReportDateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FiRefreshCw, FiFilter } from 'react-icons/fi';
import { downloadCsv, fetchAllDetailedRows } from 'helpers/exportCsv';

import SalesSummaryCards    from './SalesSummaryCards';
import PipelineStatusChart  from './PipelineStatusChart';
import QuotationStagesChart from './QuotationStagesChart';
import ConversionFunnelChart from './ConversionFunnelChart';
import LeadSourceChart      from './LeadSourceChart';
import TopContractsChart    from './TopContractsChart';
import LostProjectsChart    from './LostProjectsChart';
import SalesUnitChart       from './SalesUnitChart';
import TrendChart           from './TrendChart';
import SalesFilterSidebar   from './SalesFilterSidebar';
import SalesDetailedTable   from './SalesDetailedTable';
import DrillDownPanel       from './DrillDownPanel';
import LeadTrendChart       from './LeadTrendChart';
import UpcomingShipmentsChart from './UpcomingShipmentsChart';
import CompletionChart      from './CompletionChart';
import WaiverAnalysisChart  from './WaiverAnalysisChart';

import {
  fetchSalesSummary,
  fetchSalesPipeline,
  fetchSalesQuotation,
  fetchSalesConversion,
  fetchSalesTopContracts,
  fetchSalesLost,
  fetchSalesSalesUnit,
  fetchSalesTrend,
  fetchSalesDetailed,
  fetchSalesFilterOptions,
  fetchSalesLeadTrend,
  fetchSalesUpcomingShipments,
  fetchSalesCompletion,
  fetchSalesWaivers,
  resetError
} from 'redux/crm/dashboard/salesSlice';

const DEFAULT_START = dayjs().startOf('year').format('YYYY-MM-DD');
const DEFAULT_END   = dayjs().format('YYYY-MM-DD');
const LOAD_TIMEOUT_MS = 30000; // 30 seconds

const SalesDashboard = () => {
  const dispatch       = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pageReady, setPageReady] = useState(false);
  const [timeoutOccurred, setTimeoutOccurred] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('start_date') || DEFAULT_START,
    endDate:   searchParams.get('end_date')   || DEFAULT_END
  });

  const [filters, setFilters] = useState({
    sales_units:     searchParams.get('sales_units')?.split(',').filter(Boolean).map(Number) || [],
    sales_incharges: searchParams.get('sales_incharges')?.split(',').filter(Boolean) || [],
    customers:       searchParams.get('customers')?.split(',').filter(Boolean).map(Number) || [],
    statuses:        searchParams.get('statuses')?.split(',').filter(Boolean).map(Number) || [],
    q_j_number:      searchParams.get('q_j_number') || ''
  });

  const [groupBy,          setGroupBy]        = useState(searchParams.get('group_by') || 'month');
  const [leadGroupBy,      setLeadGroupBy]    = useState('month');
  const [detailedPage,     setDetailedPage]   = useState(1);
  const [perPage,          setPerPage]        = useState(50);
  const [sidebarOpen,      setSidebarOpen]    = useState(false);
  const [exportLoading,    setExportLoading]  = useState(false);
  const [drillDown,        setDrillDown]      = useState({ open: false, title: '', extraParams: {} });

  const {
    summary, pipeline, quotation, conversion, topContracts,
    lost, salesUnit, trend, detailed, filterOptions,
    leadTrend, upcomingShipments, completion, waivers, error
  } = useSelector(state => state.crm_dashboard_sales);

  // Detect when page is ready (first summary data arrives)
  useEffect(() => {
    if (summary.data || summary.error) {
      setPageReady(true);
    }
  }, [summary.data, summary.error]);

  // Timeout detection: fire if no data after LOAD_TIMEOUT_MS
  useEffect(() => {
    if (pageReady) return; // Already loaded, clear timeout

    const timeoutId = setTimeout(() => {
      setTimeoutOccurred(true);
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [pageReady]);

  const buildParams = useCallback((extra = {}) => {
    const p = {
      start_date: dateRange.startDate,
      end_date:   dateRange.endDate,
      ...extra
    };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units;
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges;
    if (filters.customers.length)       p.customers       = filters.customers;
    if (filters.statuses.length)        p.statuses        = filters.statuses;
    if (filters.q_j_number?.trim())     p.q_j_number      = filters.q_j_number.trim();
    return p;
  }, [dateRange, filters]);

  const baseParams = useMemo(() => buildParams(), [buildParams]);

  // Sync URL params
  useEffect(() => {
    const p = { start_date: dateRange.startDate, end_date: dateRange.endDate, group_by: groupBy };
    if (filters.sales_units.length)     p.sales_units     = filters.sales_units.join(',');
    if (filters.sales_incharges.length) p.sales_incharges = filters.sales_incharges.join(',');
    if (filters.customers.length)       p.customers       = filters.customers.join(',');
    if (filters.statuses.length)        p.statuses        = filters.statuses.join(',');
    if (filters.q_j_number?.trim())     p.q_j_number      = filters.q_j_number.trim();
    setSearchParams(p);
  }, [dateRange, filters, groupBy, setSearchParams]);

  // Fetch all sections whenever filters change
  useEffect(() => {
    dispatch(fetchSalesSummary(baseParams));
    dispatch(fetchSalesPipeline(baseParams));
    dispatch(fetchSalesQuotation(baseParams));
    dispatch(fetchSalesConversion(baseParams));
    dispatch(fetchSalesTopContracts(baseParams));
    dispatch(fetchSalesLost(baseParams));
    dispatch(fetchSalesSalesUnit(baseParams));
    dispatch(fetchSalesCompletion(baseParams));
    dispatch(fetchSalesFilterOptions(buildParams()));
    dispatch(fetchSalesUpcomingShipments(buildParams()));
    dispatch(fetchSalesWaivers(baseParams));
  }, [dispatch, baseParams, buildParams]);

  // Trend fetches separately (depends on groupBy)
  useEffect(() => {
    dispatch(fetchSalesTrend({ ...baseParams, group_by: groupBy }));
  }, [dispatch, baseParams, groupBy]);

  // Lead trend fetches separately (depends on leadGroupBy)
  useEffect(() => {
    dispatch(fetchSalesLeadTrend({ ...baseParams, group_by: leadGroupBy }));
  }, [dispatch, baseParams, leadGroupBy]);

  // Detailed fetches separately (depends on page + perPage)
  useEffect(() => {
    dispatch(fetchSalesDetailed({
      ...baseParams,
      pagination: { page: detailedPage, per_page: perPage }
    }));
  }, [dispatch, baseParams, detailedPage, perPage]);

  const handleRefresh = useCallback(() => {
    dispatch(resetError());
    dispatch(fetchSalesSummary(baseParams));
    dispatch(fetchSalesPipeline(baseParams));
    dispatch(fetchSalesQuotation(baseParams));
    dispatch(fetchSalesConversion(baseParams));
    dispatch(fetchSalesTopContracts(baseParams));
    dispatch(fetchSalesLost(baseParams));
    dispatch(fetchSalesSalesUnit(baseParams));
    dispatch(fetchSalesTrend({ ...baseParams, group_by: groupBy }));
    dispatch(fetchSalesLeadTrend({ ...baseParams, group_by: leadGroupBy }));
    dispatch(fetchSalesFilterOptions(buildParams()));
    dispatch(fetchSalesUpcomingShipments(buildParams()));
    dispatch(fetchSalesCompletion(baseParams));
    dispatch(fetchSalesWaivers(baseParams));
    dispatch(fetchSalesDetailed({ ...baseParams, pagination: { page: detailedPage, per_page: perPage } }));
  }, [dispatch, baseParams, groupBy, leadGroupBy, detailedPage, perPage, buildParams]);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setDetailedPage(1);
    setSidebarOpen(false);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({ sales_units: [], sales_incharges: [], customers: [], statuses: [], q_j_number: '' });
    setDetailedPage(1);
  }, []);

  const openDrillDown = useCallback((title, extraParams) => {
    setDrillDown({ open: true, title, extraParams });
  }, []);

  const handleExportAll = useCallback(async () => {
    setExportLoading(true);
    try {
      const rows = await fetchAllDetailedRows(baseParams, 200);
      downloadCsv(rows);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }, [baseParams]);

  const activeFilterCount = (
    filters.sales_units.length +
    filters.sales_incharges.length +
    filters.customers.length +
    filters.statuses.length +
    (filters.q_j_number?.trim() ? 1 : 0)
  );

  // Derive lead source from summary data
  const leadSourceData = useMemo(() => {
    const d = summary.data;
    if (!d) return [];
    return [
      { source: 'from_lead', label: 'From Lead',    count: d.from_lead_count || 0, value: 0 },
      { source: 'direct',    label: 'Direct Entry', count: d.direct_count    || 0, value: 0 }
    ];
  }, [summary.data]);

  // Show loading state until first data arrives
  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" message="Loading Sales Dashboard…" />
        {timeoutOccurred && (
          <Alert variant="warning" className="tw-max-w-sm">
            <p className="tw-font-semibold tw-text-sm tw-mb-2">Dashboard is taking longer than expected</p>
            <p className="tw-text-xs tw-text-gray-700 tw-mb-3">The dashboard is still loading. This may indicate a slow connection or a temporary server issue.</p>
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Alert>
        )}
      </div>
    );
  }

  const anyError = error || summary.error || pipeline.error;

  return (
    <div className="tw-min-h-screen tw-bg-slate-50">
      {/* ── Sticky header ── */}
      <div className="tw-sticky tw-z-40 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm" style={{ top: 'var(--bs-top-nav-height, 4.3125rem)' }}>
        <div className="tw-px-5 tw-py-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <div className="tw-flex-1 tw-min-w-0">
            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">Sales Dashboard</h1>
            <p className="tw-text-xs tw-text-gray-400 tw-hidden md:tw-block">
              Pipeline · Quotation · Conversion · Contracts · Lost
            </p>
          </div>

          <ReportDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={({ startDate, endDate }) => { setDateRange({ startDate, endDate }); setDetailedPage(1); }}
          />

          {/* Filter toggle */}
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
          >
            <FiFilter size={14} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-600 tw-text-white tw-text-xs tw-font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>

          <Button
            onClick={handleRefresh}
            variant="ghost"
            className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-500 hover:tw-text-gray-800"
          >
            <FiRefreshCw size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* ── Error banner ── */}
      {anyError && (
        <div className="tw-px-5 tw-pt-4">
          <Alert type="error" message="Some sections failed to load. Try refreshing." dismissible />
        </div>
      )}

      {/* ── Main layout: sidebar + content ── */}
      <div className="tw-flex tw-gap-5 tw-px-5 tw-py-5">

        {/* Filter sidebar */}
        <div className={`tw-flex-shrink-0 tw-w-64 tw-transition-all tw-duration-300 ${sidebarOpen ? 'tw-block' : 'tw-hidden md:tw-block'}`}>
          <div className="tw-sticky tw-top-20">
            <SalesFilterSidebar
              filterOptions={filterOptions.data}
              loading={filterOptions.loading}
              filters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>
        </div>

        {/* Dashboard content */}
        <div className="tw-flex-1 tw-min-w-0 tw-space-y-5">

          {/* ── 1. KPI Summary ── */}
          <SalesSummaryCards
            data={summary.data}
            loading={summary.loading}
            error={summary.error}
          />

          {/* ── 2. Pipeline Health: Status breakdown + Quotation stages ── */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <PipelineStatusChart
              data={pipeline.data}
              loading={pipeline.loading}
              error={pipeline.error}
              onDrillDown={openDrillDown}
            />
            <QuotationStagesChart
              data={quotation.data}
              loading={quotation.loading}
              error={quotation.error}
              onDrillDown={openDrillDown}
            />
          </div>

          {/* ── 3. Conversion Funnel (full width — key metric) ── */}
          <ConversionFunnelChart
            data={conversion.data}
            loading={conversion.loading}
            error={conversion.error}
            onDrillDown={openDrillDown}
          />

          {/* ── 4. Lead Analysis: source pie + creation trend ── */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <LeadSourceChart
              data={leadSourceData}
              loading={summary.loading}
              error={summary.error}
              onDrillDown={openDrillDown}
            />
            <LeadTrendChart
              data={leadTrend.data}
              loading={leadTrend.loading}
              error={leadTrend.error}
              groupBy={leadGroupBy}
              onGroupByChange={setLeadGroupBy}
            />
          </div>

          {/* ── 5. Revenue & Project Trend ── */}
          <TrendChart
            data={trend.data}
            loading={trend.loading}
            error={trend.error}
            groupBy={groupBy}
            onGroupByChange={(g) => { setGroupBy(g); setDetailedPage(1); }}
          />

          {/* ── 6. Top Contracts (full width) ── */}
          <TopContractsChart
            data={topContracts.data}
            loading={topContracts.loading}
            error={topContracts.error}
          />

          {/* ── 7. Waiver Analysis (full width) ── */}
          <WaiverAnalysisChart
            data={waivers.data}
            loading={waivers.loading}
            error={waivers.error}
          />

          {/* ── 8. Operations: Upcoming shipments + Job completion ── */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-5">
            <UpcomingShipmentsChart
              data={upcomingShipments.data}
              loading={upcomingShipments.loading}
              error={upcomingShipments.error}
            />
            <CompletionChart
              data={completion.data}
              loading={completion.loading}
              error={completion.error}
              onDrillDown={openDrillDown}
            />
          </div>

          {/* ── 9. Risk & Regions: Lost projects + Sales unit performance ── */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-5 tw-items-stretch">
            <div className="lg:tw-col-span-1 tw-h-full">
              <LostProjectsChart
                data={lost.data}
                loading={lost.loading}
                error={lost.error}
                onDrillDown={(title, extraParams) =>
                  setDrillDown({ open: true, title, extraParams })
                }
              />
            </div>
            <div className="lg:tw-col-span-2 tw-h-full">
              <SalesUnitChart
                data={salesUnit.data}
                loading={salesUnit.loading}
                error={salesUnit.error}
              />
            </div>
          </div>

          {/* ── 10. Detailed Records ── */}
          <SalesDetailedTable
            data={detailed.data}
            pagination={detailed.pagination}
            loading={detailed.loading}
            error={detailed.error}
            onPageChange={(p) => setDetailedPage(p)}
            onPageSizeChange={(s) => { setPerPage(s); setDetailedPage(1); }}
            onExportAll={handleExportAll}
            exportLoading={exportLoading}
          />
        </div>
      </div>

      {/* Drill-down panel (slide-over) */}
      <DrillDownPanel
        open={drillDown.open}
        title={drillDown.title}
        baseParams={baseParams}
        extraParams={drillDown.extraParams}
        onClose={() => setDrillDown(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default SalesDashboard;
